import { useMemo } from "react";
import { useGetQualificationsQuery } from "../../../common/api/qualification";
import { useEstablishmentId } from "../../../common/utils/useEstablishmentId";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getFilterTabs } from "../utils/getFilterTabs";
import { getQualificationTypeLabel } from "../../../common/enums/QualificationTypeEnum";
import { setMissingCertificatesWidgetCurrentPage } from "../DashboardSlice";
import { mapMissingQualificationsToRows } from "../utils/mapMissingQualificationsToRows";
import { TableWidgetRow } from "@hatch-digital-nl/abs-storybook/legacy";
import { getFilterTabs as getFilterTabsFn } from "../utils/getFilterTabs";

type UseDashboardMissingQualificationsResult = {
    loading: boolean;
    table: {
        columns: string[];
        rows: TableWidgetRow[];
        total: number;
    };
    currentPage: number;
    onPageChange: (page: number) => void;
    filterTabs: ReturnType<typeof getFilterTabsFn>;
    emptyResultText: string;
};

export const useDashboardMissingQualifications =
    (): UseDashboardMissingQualificationsResult => {
        const dispatch = useAppDispatch();
        const establishmentId = useEstablishmentId();

        const {
            missingCertificatesWidget: { currentPage, filters },
        } = useAppSelector((state) => state.dashboard);

        const { data, isLoading, isFetching } = useGetQualificationsQuery({
            filter: {
                establishment_missing_standards: establishmentId,
                type: filters.activeTab,
            },
            page: currentPage,
            items_per_page: 12,
        });

        const loading = isLoading || isFetching;

        const table = useMemo(() => {
            return {
                columns: ["Naam", "Norm"] as string[],
                rows: mapMissingQualificationsToRows({
                    missingQualifications: data?.data,
                    dispatch,
                }),
                total: data?.meta?.total ?? 0,
            };
        }, [data, dispatch]);

        const onPageChange = (page: number): void => {
            dispatch(setMissingCertificatesWidgetCurrentPage(page));
        };

        const filterTabs = getFilterTabs(dispatch, filters.activeTab);
        const emptyResultText = `Er zijn geen missende kwalificaties voor het type ${getQualificationTypeLabel(filters.activeTab).toLowerCase()}`;

        return {
            loading,
            table,
            currentPage,
            onPageChange,
            filterTabs,
            emptyResultText,
        };
    };
